import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types'
import { useLocation } from 'react-router-dom'
import ProviderNotVerifiedPage from './ProviderNotVerifiedPage'
import ProviderBlockedPage from './ProviderBlockedPage'
import { isVerificationRequiredForRoute } from '@/config/providerVerificationRoutes'
import { User } from '@/types'
import api from '@/services/api'
import SubscriptionRequiredPopup from './SubscriptionRequiredPopup'
import { useAppDispatch } from '@/store/index'
import { setProviderProfileInfo } from '@/store/slices/appSlice'

interface ProviderVerificationGuardProps {
  children: React.ReactNode
}

/**
 * Provider account status types
 */
enum ProviderStatus {
  ACTIVE = 'active', // status is active
  BLOCKED = 'blocked', // status is blocked
  PENDING = 'pending', // status is pending
}

/**
 * Guard component that ensures providers are verified and not blocked
 * before accessing certain protected routes.
 *
 * This component checks three states:
 * 1. ACTIVE: status is active → Access granted
 * 2. BLOCKED: status is blocked → Show blocked page
 * 3. PENDING: status is pending → Show verification pending page
 *
 * The guard checks:
 * - If the user is a provider/therapist
 * - If the current route requires verification
 * - The provider's status
 */
const ProviderVerificationGuard: React.FC<ProviderVerificationGuardProps> = ({
  children,
}) => {
  const { user, role } = useAuth()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const [isChecking, setIsChecking] = useState(true);
  const [latestUserData, setLatestUserData] = useState<User | null>(null);
  const [agencyDataName, setAgencyDataName] = useState<string>('');

  useEffect(() => {
    const checkVerificationStatus = () => {
      // Only check for therapist role
      if (role !== UserRole.THERAPIST || !user) {
        setIsChecking(false)
        return
      }

      // Check if current route requires verification
      const requiresVerification = isVerificationRequiredForRoute(
        location.pathname
      )

      if (!requiresVerification) {
        setIsChecking(false)
        return
      }

      // Verification check complete
      setIsChecking(false)
    }

    checkVerificationStatus()
  }, [user, role, location.pathname])

  // fecthProfile api call to get latest user data including verification status
  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/therapist/profile')
      const profileData = response?.data?.data
      const agencyData = response?.data?.agency
      setLatestUserData(profileData)
      setAgencyDataName(agencyData?.agencyName)
      dispatch(setProviderProfileInfo({
        providerWorkWith: profileData?.providerWorkWith || null,
        providerHasAgency: (agencyData !== null && agencyData !== undefined) || (profileData?.agencies && profileData.agencies.length > 0),
      }))
    } catch (error) {
      console.error('Error fetching user profile:', error)
    }
  }

  useEffect(() => {
    if (role === UserRole.THERAPIST) {
      fetchUserProfile()
    }
  }, [role])

  /**
   * Determine provider status based on status field
   */
  const getProviderStatus = (): ProviderStatus => {
    const status = latestUserData?.status || (user as any)?.status || 'pending'
    return status as ProviderStatus
  }

  // Check if current route requires verification
  const requiresVerification = isVerificationRequiredForRoute(location.pathname)

  // Show loading while checking verification status or fetching user data
  if (isChecking || (role === UserRole.THERAPIST && !latestUserData && requiresVerification)) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Verifying access permissions...
        </Typography>
      </Box>
    )
  }

  // Only enforce verification for therapist role
  if (role !== UserRole.THERAPIST) {
    return <>{children}</>
  }

  // Check if current route requires verification


  // If route doesn't require verification, render children
  if (!requiresVerification) {
    return <>{children}</>
  }

  // Determine provider status
  const providerStatus = getProviderStatus()
  const providerName = user ? `${user.firstName} ${user.lastName}` : undefined

  // Check if user is switched to/associated with an agency via localStorage
  let hasSwitchedAgency = false
  try {
    const authUserStr = localStorage.getItem('auth-user')
    if (authUserStr) {
      const parsed = JSON.parse(authUserStr)
      if (parsed?.agencyId) {
        hasSwitchedAgency = true
      }
    }
  } catch (e) {
    console.error('Error parsing auth-user in ProviderVerificationGuard:', e)
  }

  if (
    latestUserData &&
    latestUserData.isSubscriptionTaken === false &&
    latestUserData.providerWorkWith !== 'agency' &&
    !latestUserData.agencyId &&
    !user?.agencyId &&
    !hasSwitchedAgency &&
    (!agencyDataName || agencyDataName === "N/A")
  ) {
    return <SubscriptionRequiredPopup providerName={providerName} />
  } else {

    // Handle each status appropriately
    switch (providerStatus) {
      case ProviderStatus.ACTIVE:
        // Provider is active - grant access
        return <>{children}</>

      case ProviderStatus.BLOCKED:
        // Provider is blocked - show blocked page
        return (
          <ProviderBlockedPage
            providerName={providerName}
            blockReason={(user as any)?.blockReason}
          />
        )

      case ProviderStatus.PENDING:
        // Provider is pending - show verification pending page
        return <ProviderNotVerifiedPage providerName={providerName} />

      default:
        // Provider is not verified - show verification pending page
        return <ProviderNotVerifiedPage providerName={providerName} />
    }
  }
}

export default ProviderVerificationGuard
